<template>
  <Component
    :is="maxSmall ? Drawer : Dialog"
    :open="open"
    @update:open="$emit('update:open', $event)"
    variant="log"
    background="white"
    :showFooter="true"
    :confirmLabel="$t('components.buttons.submit')"
    :disableConfirm="disableSubmit"
    :closeOnConfirm="false"
    @confirm="logMaterial"
  >
    <slot />
    <template #header>
      <Component
        :is="maxTablet ? DrawerHeader : DialogHeader"
        :title="$t('components.task_details.log_material.title')"
        :description="
          maxTablet
            ? undefined
            : $t('components.task_details.log_material.description')
        "
        :class="{ 'px-2 border-b': maxTablet }"
      />
    </template>
    <template #content>
      <div
        class="max-h-56 md:max-h-64 lg:max-h-88 flex flex-col py-4 px-5 md:px-6 bg-tertiary overflow-y-auto"
      >
        <FileUpload
          v-model:files="form.files"
          :buildingId="task.tenant.id"
          multiple
        />
      </div>
      <div
        class="flex flex-col grow pt-6 px-5 md:px-6 max-md:pb-6 gap-4 overflow-y-auto"
      >
        <div class="flex grow gap-4">
          <div class="grow basis-1/2">
            <DateInputSelect
              v-model="form.date"
              :label="$t('components.task_details.log_material.form.date')"
              :size="maxTablet ? 'lg' : 'sm'"
              :maxDate="new Date()"
              :required="true"
              :errorMessage="errors.date"
              alignPopover="center"
            />
          </div>
          <div class="grow basis-1/2">
            <MonetaryInput
              v-model="form.cost"
              :label="$t('components.task_details.log_material.form.cost')"
              :size="maxTablet ? 'lg' : 'sm'"
              :errorMessage="errors.cost"
            />
          </div>
        </div>
        <InputDropdown
          v-model="form.user"
          :label="$t('components.task_details.log_material.form.select_user')"
          :placeholder="
            $t('components.task_details.log_material.form.select_user')
          "
          icon="user-single"
          :groups="[
            {
              options: userOptions,
            },
          ]"
          :size="maxTablet ? 'lg' : 'sm'"
        />
        <TextAreaInput
          v-model="form.description"
          :label="$t('components.task_details.log_material.form.description')"
          :placeholder="
            $t(
              'components.task_details.log_material.form.description_placeholder'
            )
          "
          :size="maxTablet ? 'md' : 'sm'"
          :rows="4"
        />
      </div>
    </template>
  </Component>
</template>

<script setup>
import { computed, watch } from "vue";
import { useForm } from "@inertiajs/vue3";
import { Dialog, DialogHeader } from "@/SharedComponents/Dialog";
import { Drawer, DrawerHeader } from "@/SharedComponents/Drawer";
import { InputDropdown } from "@/SharedComponents/InputDropdown";
import {
  DateInputSelect,
  FileUpload,
  MonetaryInput,
  TextAreaInput,
} from "@/SharedComponents/Inputs";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { toUTC } from "@/Utils/date";

const { maxSmall, maxTablet } = useBreakpoints();

const emit = defineEmits(["update:open", "logMaterial"]);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  task: { type: Object, required: true },
  users: { type: Array, required: true },
  errors: { type: Object, default: () => ({}) },
  busy: { type: Boolean, default: false },
});

const form = useForm({
  date: new Date(),
  cost: null,
  user: [],
  description: "",
  files: [],
});

watch(
  () => props.open,
  () => {
    if (props.open) {
      form.reset();
    }
  }
);

const userOptions = computed(() => {
  return props.users.map((u) => ({ value: u.id, label: u.fullname }));
});

function logMaterial() {
  emit("logMaterial", {
    date: toDateStr(form.date),
    cost: form.cost,
    user: form.user[0].value,
    description: form.description,
    files: form.files,
  });
}

function toDateStr() {
  const datetime = new Date(form.date);
  datetime.setHours(0, 0, 0, 0);
  return toUTC(datetime.toISOString());
}

const disableSubmit = computed(
  () => !form.date || !form.cost || !form.user?.length || props.busy
);
</script>
